import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";
import { Plus, Trash2 } from "lucide-react";

import { listGateways } from "@/api/catalog";
import {
  acceptPendingImport,
  createComponent,
  getComponent,
  getComponentUsage,
  listComponents,
  listImageRepositories,
  updateComponent,
} from "@/api/components";
import { ConfirmModal } from "@/components/ConfirmModal";
import { ImportedFieldsNotice } from "@/components/ImportedFieldsNotice";
import { JsonPayloadEditor } from "@/components/JsonPayloadEditor";
import { Field, Fieldset, Input, Select } from "@/components/form";
import { MutationError, RefreshFailed, isLoadingOrPaused, loadFailure } from "@/components/Spinner";
import {
  GROUP_LABEL,
  isCapabilityGroup,
  splitImageUri,
} from "@/lib/catalogComponents";
import {
  COMPONENT_GROUPS,
  type ComponentParam,
  type ComponentUsage,
  type GatewayComponent,
  type GatewayComponentGroup,
} from "@/types/api";
import { Button, FormActionBar, FormPlaceholder, IconButton, Notice, Page, PageHeader } from "@/components/ui";
import { useDirtyValues } from "@/hooks/useDirtyValues";
import { useUnsavedChanges } from "@/hooks/useUnsavedChanges";

const GROUPS = COMPONENT_GROUPS.map((group) => ({
  value: group,
  label: GROUP_LABEL[group],
}));

// Hapsout central account (README.md), eu-west-1 — the shared image registry
// every component's image lives in. Used only when composing the image_uri
// of a brand-new component and no sibling component in the catalog carries
// an existing URI to borrow the account/region prefix from.
const FALLBACK_REGISTRY_PREFIX = "771033850854.dkr.ecr.eu-west-1.amazonaws.com";

function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? "" : "s"}`;
}

/**
 * Impact banner shown above the form, before any editing — mirrors the
 * catalog entry's impact when deleting (CatalogGateways.tsx), but for a
 * component. ``tenants`` (not "stages"): the backend's
 * find_tenants_referencing_gateway only ever returns deduplicated tenant
 * ids, never stage names — see ComponentUsage in types/api.ts.
 *
 * It reads the *pending* inclusion, not the stored one. Inclusion is the
 * only field whose edit rewrites the document of gateway types that nobody
 * touched — composition is resolved at render time, so an always-included
 * component ships with every type without any recipe listing it. A banner
 * that only ever showed the scope as it stands would describe the state the
 * operator is about to leave, never the one they are about to create — and
 * the most destructive gesture of the page (switching the base off) would
 * be the one it says the least about.
 */
function ImpactBanner({
  usage,
  storedInclusion,
  pendingInclusion,
  customerTypeCount,
}: {
  usage: ComponentUsage | undefined;
  storedInclusion: "always" | "optional" | null;
  pendingInclusion: "always" | "optional";
  customerTypeCount: number | null;
}) {
  if (!usage) return null;
  const types = usage.types.length;
  const tenants = usage.tenants.length;
  const tenantClause =
    tenants > 0
      ? ` The configuration delivered to ${plural(tenants, "tenant")} changes.`
      : "";

  if (storedInclusion === "always" && pendingInclusion === "optional") {
    return (
      <Notice
        tone="danger"
        title="Saving removes this component from every gateway type that ships it."
        className="mb-4"
      >
        <p className="mt-1">
          It is part of the base today, so all {plural(types, "gateway type")}{" "}
          carry it without listing it. Making it optional drops it from all of
          them at once, and nothing downstream fails — the types keep
          composing, just without this component.{tenantClause}
        </p>
      </Notice>
    );
  }

  if (storedInclusion === "optional" && pendingInclusion === "always") {
    return (
      <Notice
        tone="danger"
        title="Saving adds this component to every gateway type."
        className="mb-4"
      >
        <p className="mt-1">
          {customerTypeCount === null
            ? "Every gateway type"
            : `All ${plural(customerTypeCount, "gateway type")}`}{" "}
          would ship it from then on.
          {types > 0 && (
            <>
              {" "}
              {plural(types, "type")} already{" "}
              {types === 1 ? "lists" : "list"} it in{" "}
              {types === 1 ? "its" : "their"} recipe — a component can't be
              both part of the base and picked by a recipe, so the save will
              be refused until it is unchecked there.
            </>
          )}
          {tenantClause}
        </p>
      </Notice>
    );
  }

  if (types === 0) {
    return (
      <Notice tone="info" className="mb-4">
        Not used by any gateway type yet.
      </Notice>
    );
  }
  return (
    <Notice tone="warning" className="mb-4">
      Used by {plural(types, "gateway type")}
      {tenants > 0 && (
        <>
          {" "}— saving will change the configuration delivered to{" "}
          {plural(tenants, "tenant")}
        </>
      )}
      .
    </Notice>
  );
}

// Local editable shape for the params list — ``_id`` is a client-only React
// key (stable across reorders/edits of ``key``, which the operator is
// actively typing into and may transiently collide or be empty) and is
// stripped before the params are sent to the API.
interface ParamRow extends ComponentParam {
  _id: number;
}

function ParamsEditor({
  rows,
  onChange,
}: {
  rows: ParamRow[];
  onChange: (rows: ParamRow[]) => void;
}) {
  function update(id: number, patch: Partial<ComponentParam>) {
    onChange(rows.map((r) => (r._id === id ? { ...r, ...patch } : r)));
  }
  function remove(id: number) {
    onChange(rows.filter((r) => r._id !== id));
  }

  return (
    <div className="space-y-2">
      {rows.length > 0 && (
        // The last column is the remove button's width (an IconButton is 2.25rem), fixed in
        // BOTH grids: with `auto`, the header's empty cell was narrower than the rows' button
        // and the two grids — separate boxes — lined their columns up differently.
        <div className="grid grid-cols-[1fr_5rem_5rem_5rem_2.25rem] gap-2 text-xs font-medium text-ink-muted">
          <span>Key</span>
          <span>Min</span>
          <span>Max</span>
          <span>Default</span>
          <span />
        </div>
      )}
      {rows.map((row, i) => (
        <div key={row._id} className="grid grid-cols-[1fr_5rem_5rem_5rem_2.25rem] gap-2">
          {/* The header row above is the visible label; each cell still names
              its parameter and its column for assistive technology. */}
          <Input
            mono
            aria-label={`Parameter ${i + 1}, key`}
            placeholder="e.g. number"
            value={row.key}
            onChange={(e) => update(row._id, { key: e.target.value })}
          />
          <Input
            type="number"
            aria-label={`Parameter ${i + 1}, minimum`}
            value={row.minimum}
            onChange={(e) => update(row._id, { minimum: Number(e.target.value) })}
          />
          <Input
            type="number"
            aria-label={`Parameter ${i + 1}, maximum`}
            value={row.maximum}
            onChange={(e) => update(row._id, { maximum: Number(e.target.value) })}
          />
          <Input
            type="number"
            aria-label={`Parameter ${i + 1}, default`}
            value={row.default}
            onChange={(e) => update(row._id, { default: Number(e.target.value) })}
          />
          <IconButton
            icon={Trash2}
            label={`Remove parameter ${i + 1}`}
            onClick={() => remove(row._id)}
          />
        </div>
      ))}
    </div>
  );
}

export function CatalogComponentEditPage() {
  const routeParams = useParams<{ group?: string; name?: string }>();
  const isEdit = Boolean(routeParams.group && routeParams.name);
  const routeGroup = (routeParams.group as GatewayComponentGroup | undefined) ?? null;
  const routeName = routeParams.name ?? null;

  const auth = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const token = auth.user?.id_token ?? "";

  const queryKey = ["catalog", "component", routeGroup, routeName] as const;

  const existing = useQuery({
    queryKey,
    queryFn: () => getComponent(token, routeGroup!, routeName!),
    enabled: !!token && isEdit,
  });

  // Mounted independently of ``existing`` so the impact banner renders
  // immediately, before the rest of the form has loaded.
  const usageQuery = useQuery({
    queryKey: ["catalog", "component-usage", routeGroup, routeName],
    queryFn: () => getComponentUsage(token, routeGroup!, routeName!),
    enabled: !!token && isEdit,
  });

  const repositoriesQuery = useQuery({
    queryKey: ["catalog", "image-repositories"],
    queryFn: () => listImageRepositories(token),
    enabled: !!token,
    staleTime: 5 * 60_000,
  });

  // Only read to size the "adds it to every gateway type" side of the
  // inclusion impact. Same query key as the catalog list page, so the cache
  // is shared rather than re-fetched.
  const gatewaysQuery = useQuery({
    queryKey: ["catalog", "gateways"],
    queryFn: () => listGateways(token),
    enabled: !!token && isEdit,
  });
  const customerTypeCount = gatewaysQuery.data
    ? gatewaysQuery.data.items.filter((i) => i.kind === "customer").length
    : null;

  // Every other component in the catalog — used only to infer the shared
  // registry's account/region prefix for a brand-new component that has no
  // image_uri of its own yet to preserve the prefix from.
  const siblingsQuery = useQuery({
    queryKey: ["catalog", "components"],
    queryFn: () => listComponents(token),
    enabled: !!token && !isEdit,
  });

  const [group, setGroup] = useState<GatewayComponentGroup>(routeGroup ?? "inputs");
  const [name, setName] = useState<string>(routeName ?? "");
  const [description, setDescription] = useState("");
  const [inclusion, setInclusion] = useState<"always" | "optional">("optional");
  const [capabilityKey, setCapabilityKey] = useState("");
  const [capabilityJson, setCapabilityJson] = useState("{\n\n}\n");
  const [capabilityJsonValid, setCapabilityJsonValid] = useState(true);
  const [bodyJson, setBodyJson] = useState("{\n\n}\n");
  const [bodyJsonValid, setBodyJsonValid] = useState(true);
  const [imageRepo, setImageRepo] = useState("");
  const [preservedPrefix, setPreservedPrefix] = useState<string | null>(null);
  const [params, setParams] = useState<ParamRow[]>([]);
  const [primed, setPrimed] = useState(false);
  const [confirmDemotion, setConfirmDemotion] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Dirty = what the form shows differs from what it was opened with (the values of the render where
  // it was primed), and the way out of the page is held while it is. ``_id`` is a React key, not a
  // value: it stays out of what is compared.
  const { dirty, reset: markSaved } = useDirtyValues(
    {
      group,
      name,
      description,
      inclusion,
      capabilityKey,
      capabilityJson,
      bodyJson,
      imageRepo,
      params: params.map(({ key, minimum, maximum, default: def }) => ({ key, minimum, maximum, default: def })),
    },
    primed,
  );
  const guard = useUnsavedChanges(dirty);

  const paramIdCounter = useRef(0);
  function nextParamId(): number {
    paramIdCounter.current += 1;
    return paramIdCounter.current;
  }

  useEffect(() => {
    setPrimed(false);
  }, [routeGroup, routeName]);

  useEffect(() => {
    if (primed) return;
    if (isEdit && existing.data) {
      const component = existing.data;
      setGroup(component.group);
      setName(component.name);
      setInclusion(component.inclusion);
      setCapabilityKey(component.capabilityKey ?? "");
      setCapabilityJson(JSON.stringify(component.capability ?? {}, null, 2));
      setParams(component.params.map((p) => ({ ...p, _id: nextParamId() })));

      const body = { ...component.body };
      const rawDescription = body.description;
      setDescription(typeof rawDescription === "string" ? rawDescription : "");
      delete body.description;

      const rawImageUri = body.image_uri;
      if (typeof rawImageUri === "string" && rawImageUri) {
        const split = splitImageUri(rawImageUri);
        if (split) {
          setPreservedPrefix(split.prefix);
          setImageRepo(split.repository);
        } else {
          // Not ECR-shaped (unexpected) — leave the selector empty rather
          // than guess, but keep the raw value in the JSON block so nothing
          // is silently dropped.
          setImageRepo("");
        }
      }
      delete body.image_uri;
      setBodyJson(JSON.stringify(body, null, 2));
      setPrimed(true);
      return;
    }
    if (!isEdit) {
      setPrimed(true);
    }
  }, [existing.data, isEdit, primed]);

  // Registry prefix to compose a new image_uri with: this component's own
  // preserved prefix (edit mode) wins, otherwise borrow the prefix any
  // sibling component in the catalog already uses (same shared account for
  // everyone), otherwise the documented fallback.
  const registryPrefix = useMemo(() => {
    if (preservedPrefix) return preservedPrefix;
    for (const c of siblingsQuery.data?.items ?? []) {
      const uri = c.body.image_uri;
      if (typeof uri === "string") {
        const split = splitImageUri(uri);
        if (split) return split.prefix;
      }
    }
    return FALLBACK_REGISTRY_PREFIX;
  }, [preservedPrefix, siblingsQuery.data]);

  const groupCarriesCapability = isCapabilityGroup(group);

  const mut = useMutation({
    mutationFn: (confirmImpactedTypes: number | undefined) => {
      let restBody: Record<string, unknown>;
      try {
        restBody = JSON.parse(bodyJson);
      } catch (err) {
        throw new Error(`Runtime block is not valid JSON: ${(err as Error).message}`);
      }
      if (typeof restBody !== "object" || restBody === null || Array.isArray(restBody)) {
        throw new Error("Runtime block must be a JSON object");
      }
      // ``description`` and ``image_uri`` are edited by their own fields
      // above, so the form owns them — but an empty field means "absent",
      // not "present and empty". Writing ``description: ""`` back would add
      // a key to the dataplane's document that was never in it.
      const body: Record<string, unknown> = { ...restBody };
      if (description) {
        body.description = description;
      } else {
        delete body.description;
      }
      if (imageRepo) {
        body.image_uri = `${registryPrefix}/${imageRepo}`;
      } else {
        delete body.image_uri;
      }

      let capability: Record<string, unknown> | null = null;
      if (groupCarriesCapability) {
        let parsedCapability: unknown;
        try {
          parsedCapability = JSON.parse(capabilityJson);
        } catch (err) {
          throw new Error(
            `Capability contribution is not valid JSON: ${(err as Error).message}`,
          );
        }
        if (
          typeof parsedCapability === "object" &&
          parsedCapability !== null &&
          !Array.isArray(parsedCapability) &&
          Object.keys(parsedCapability).length > 0
        ) {
          capability = parsedCapability as Record<string, unknown>;
        }
      }

      const cleanParams: ComponentParam[] = params
        .filter((p) => p.key.trim())
        .map(({ key, minimum, maximum, default: def }) => ({
          key,
          minimum,
          maximum,
          default: def,
        }));

      const payload: Omit<
        GatewayComponent,
        "updatedAt" | "source" | "pendingImport"
      > = {
        group,
        name,
        body,
        capabilityKey: groupCarriesCapability && capabilityKey.trim() ? capabilityKey.trim() : null,
        capability,
        inclusion,
        params: cleanParams,
      };

      return isEdit
        ? updateComponent(
            token,
            { ...payload, updatedAt: existing.data!.updatedAt },
            confirmImpactedTypes,
          )
        : createComponent(token, payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["catalog", "components"] });
      qc.invalidateQueries({ queryKey });
      markSaved();
      guard.leave("/catalog/components");
    },
    // Close the confirmation so the form's error box is the one thing on
    // screen — a modal still up over a failed save reads as "still going".
    onError: () => setConfirmDemotion(false),
  });

  // ``source`` present ⇒ the build owns description/settings/runtime/
  // depends_on/image_uri/image_tag AND ``capability_key`` — it will overwrite
  // them at the next build regardless of what's typed here, so those fields go
  // read-only rather than let a correction disappear silently.
  //
  // ``capability_key`` is the one that does not live in ``body``: it is a
  // component field, written by ``plan_import`` as
  // ``capability_key or stored.capability_key``. It shipped editable and was
  // caught on the first real import (2026-08-29) — a manual edit here would
  // have been handed back by the next build without a word, exactly what the
  // read-only state exists to prevent.
  //
  // The ``capability`` JSON block and ``params`` are genuinely never touched by
  // an import, so they stay editable either way. ``inclusion`` stays editable
  // too, and deliberately: it is the fleet guard a human owns.
  const source = isEdit ? existing.data?.source ?? null : null;
  const isImported = source !== null;

  const acceptMut = useMutation({
    mutationFn: () => acceptPendingImport(token, routeGroup!, routeName!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["catalog", "components"] });
      qc.invalidateQueries({ queryKey });
    },
  });

  const storedInclusion = isEdit && existing.data ? existing.data.inclusion : null;
  // Switching the base off is the one edit that changes every type's
  // document while breaking nothing — it needs an explicit acknowledgement
  // carrying the number of impacted types, which the backend re-checks.
  const isDemotion = storedInclusion === "always" && inclusion === "optional";

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitError(null);
    // A failure left by an earlier attempt must not sit next to the message of this one.
    mut.reset();
    if (!isDemotion) {
      mut.mutate(undefined);
      return;
    }
    if (!usageQuery.data) {
      setSubmitError(
        "Still measuring which gateway types ship this component — retry in a moment.",
      );
      return;
    }
    setConfirmDemotion(true);
  }

  // The title comes from the address, so loading and failing both keep it.
  const header = (
    <PageHeader
      back={{ to: "/catalog/components", label: "Components" }}
      title={isEdit ? `Edit ${group}/${name}` : "New component"}
    />
  );
  if (isEdit && isLoadingOrPaused(existing)) return <FormPlaceholder header={header} />;
  // Only an error with nothing to show replaces the page (see RefreshFailed):
  // a failed refetch must not hide the form the operator is filling in.
  if (isEdit && existing.isLoadingError) {
    return <FormPlaceholder header={header} failure={loadFailure(existing)} />;
  }

  const repoOptions = (repositoriesQuery.data?.items ?? []).map((repo) => ({
    value: repo,
    label: repo,
  }));
  const jsonValid = bodyJsonValid && (!groupCarriesCapability || capabilityJsonValid);
  const impactedTypeCount = usageQuery.data?.types.length ?? 0;

  return (
    <Page width="form">
      {header}

      <RefreshFailed query={existing} className="mb-6" />

      {isEdit && (
        <ImportedFieldsNotice
          source={source}
          pendingImport={existing.data?.pendingImport ?? null}
          pendingAction={
            <Button
              variant="primary"
              loading={acceptMut.isPending}
              onClick={() => acceptMut.mutate()}
            >
              {acceptMut.isPending ? "Accepting…" : "Accept"}
            </Button>
          }
        />
      )}
      <MutationError mutation={acceptMut} title="Could not accept the import" className="mb-4" />

      {isEdit && (
        <ImpactBanner
          usage={usageQuery.data}
          storedInclusion={storedInclusion}
          pendingInclusion={inclusion}
          customerTypeCount={customerTypeCount}
        />
      )}

      <form onSubmit={handleSubmit}>
        <div className="card space-y-5">
          <Field
            label="Group"
            help={
              isEdit
                ? "Group cannot be changed — it's part of the component's identity."
                : "Core and features components form the base every gateway type includes."
            }
          >
            <Select
              value={group}
              disabled={isEdit}
              onChange={(e) => setGroup(e.target.value as GatewayComponentGroup)}
              options={GROUPS}
            />
          </Field>

          <Field
            label="Name"
            help={
              isEdit
                ? "Name cannot be changed — it's part of the component's identity."
                : undefined
            }
          >
            <Input
              mono
              value={name}
              required
              disabled={isEdit}
              maxLength={64}
              onChange={(e) => setName(e.target.value)}
            />
          </Field>

          <Field
            label="Description"
            help={
              isImported
                ? "Owned by the import — edit the compose service to change it."
                : undefined
            }
          >
            <Input
              value={description}
              disabled={isImported}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Field>

          <Field
            label="Inclusion"
            help="Always-included components form the locked base every gateway type ships with — an operator composing a type can't uncheck them."
          >
            <Select
              value={inclusion}
              onChange={(e) => setInclusion(e.target.value as "always" | "optional")}
              options={[
                { value: "always", label: "Always included" },
                { value: "optional", label: "Optional" },
              ]}
            />
          </Field>

          <Field
            label="Image repository"
            help={
              isImported
                ? "Owned by the import — edit the compose service to change it."
                : "The shared registry account/region is preserved automatically — only the repository changes here."
            }
          >
            <Select
              value={imageRepo}
              onChange={(e) => setImageRepo(e.target.value)}
              options={repoOptions}
              disabled={isImported}
              placeholder="— none —"
            />
          </Field>

          {groupCarriesCapability && (
            <Field
              label="Capability key"
              help={
                isImported
                  ? "Owned by the import — edit the compose service to change it."
                  : "Key this component contributes under in the composed capabilities block. Leave empty if this component doesn't advertise a capability."
              }
            >
              <Input
                mono
                value={capabilityKey}
                placeholder="e.g. http"
                disabled={isImported}
                onChange={(e) => setCapabilityKey(e.target.value)}
              />
            </Field>
          )}

          <Fieldset
            legend="Parameters"
            help="Values a gateway type can tune when it selects this component (e.g. a channel count). Each becomes a bounded number field in the type composer."
          >
            <div className="mt-2">
              <ParamsEditor
                rows={params}
                onChange={setParams}
              />
              <Button
                variant="secondary"
                icon={Plus}
                className="mt-2"
                onClick={() => setParams((p) => [...p, { _id: nextParamId(), key: "", minimum: 1, maximum: 8, default: 1 }])}
              >
                Add parameter
              </Button>
            </div>
          </Fieldset>

          {groupCarriesCapability && (
            <details className="rounded border border-line">
              <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-ink">
                Capability contribution — advanced
              </summary>
              <div className="border-t border-line p-3">
                <JsonPayloadEditor
                  ariaLabel="Capability contribution (JSON)"
                  value={capabilityJson}
                  onChange={setCapabilityJson}
                  onValidity={setCapabilityJsonValid}
                  minHeight={160}
                />
                <p className="mt-1 text-xs text-ink-muted">
                  Fixed values contributed under the capability key above, on
                  top of the parameters. Rarely needed — most components only
                  use parameters.
                </p>
              </div>
            </details>
          )}

          <details className="rounded border border-line">
            <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-ink">
              Runtime (docker-compose) — advanced
            </summary>
            <div className="border-t border-line p-3">
              <JsonPayloadEditor
                ariaLabel="Runtime, docker-compose (JSON)"
                value={bodyJson}
                onChange={setBodyJson}
                onValidity={setBodyJsonValid}
                minHeight={300}
                readOnly={isImported}
              />
              <p className="mt-1 text-xs text-ink-muted">
                {isImported
                  ? "Owned by the import — edit the compose service to change it."
                  : "Everything the dataplane needs to run this component " +
                    "(image tag, environment, dependencies, …) besides " +
                    "description and image repository, which are set above."}
              </p>
            </div>
          </details>
        </div>

        <FormActionBar
          dirty={dirty}
          submitLabel={isEdit ? "Save" : "Create component"}
          pending={mut.isPending}
          disabled={!jsonValid}
          disabledReason="Invalid JSON — fix before saving"
          onCancel={() => navigate("/catalog/components")}
          error={
            <>
              {submitError && (
                <Notice tone="danger" role="alert">
                  {submitError}
                </Notice>
              )}
              <MutationError mutation={mut} title="Could not save the component" />
            </>
          }
        />
      </form>

      {confirmDemotion && (
        <ConfirmModal
          title="Remove this component from the base"
          confirmLabel="Remove from every type"
          pendingLabel="Saving…"
          danger
          pending={mut.isPending}
          onCancel={() => {
            mut.reset();
            setConfirmDemotion(false);
          }}
          onConfirm={() => mut.mutate(impactedTypeCount)}
        >
          <p>
            <code className="font-mono">
              {group}/{name}
            </code>{" "}
            is included in every gateway type today. Making it optional drops
            it from all {plural(impactedTypeCount, "type")} at once — none of
            them lists it, so none of them can keep it.
          </p>
          <p>
            Nothing will fail: the types keep composing, just without this
            component. The gateways that receive them will simply stop
            shipping it.
          </p>
        </ConfirmModal>
      )}
    </Page>
  );
}
